import Vue from 'vue'
import Vuex from 'vuex'
import {Storage} from "@/Services/Storage";
import agentModule from "@/store/modules/AgentModule";

import authModule from './modules/authModule';
import kycModule from './modules/kycModule';
import paymentModule from './modules/paymentModule';
import walletModule from './modules/walletModule';
import financialReportsModule from './modules/financialReportsModule';
import requests from './modules/requests';
import timeModule from "@/store/modules/time-module";

Vue.use(Vuex)
export default new Vuex.Store({
  state: {
    loading: false,
    dashboardData: {
      totalApprovedSignups: 0,
      pendingSignups: 0,
      totalAgentEarnings: 0,
      totalWalletBalance: 0,
      totalDeclinedKYCs: 0,
      companyApprovedSignups: 0,
      companyPendingSignups: 0,
      companyAgentEarnings: 0,
      companyWalletBalance: 0,
      companyDeclinedKYCs: 0,
      companyName: '',
      nextPaymentDate: '',
      nextPaymentAmount: 0,
      paymentFrequency: '',
      companies: [],
      transactions: [],
      kycSubmissions: [],
      kycStatusChart: {
        labels: [],
        datasets: []
      },
      paymentStatusChart: {
        labels: [],
        datasets: []
      },
      companyPerformanceChart: {
        labels: [],
        datasets: []
      },
      agentPerformanceChart: {
        labels: [],
        datasets: []
      }
    },
    dashboardLoading: false,
    dashboardError: null,
    user: JSON.parse(localStorage.getItem('user')) || null,
    isAuthenticated: false,
    agentCount: 0,
    requestCount: 0,
    kycSignups: [],
    payments: [],
    banks: [],
  },
  getters: {
    getLoadingState: (state) => state.loading,
    getDashboardData: (state) => state.dashboardData,
    getDashboardLoading: (state) => state.dashboardLoading,
    getDashboardError: (state) => state.dashboardError,
    getBanks: (state) => state.banks,
    isAuthenticated: (state) => state.isAuthenticated,
    currentUser: (state, getters) => {
      return state.auth.user || JSON.parse(localStorage.getItem('user')) || null;
    },
    userName: (state, getters) => {
      const user = getters.currentUser;
      return user?.name || '';
    },
    userEmail: (state, getters) => {
      const user = getters.currentUser;
      return user?.email || '';
    },
    userCreatedAt: (state, getters) => {
      const user = getters.currentUser;
      return user?.created_at || '';
    },
    userLevel: (state, getters) => {
      const user = getters.currentUser;
      return user?.level ? user.level.toString().trim().toUpperCase() : null;
    },
    isSuperAdmin: (state, getters) => {
      const level = getters.userLevel;
      return level === 'SUPERADMIN';
    },
    isCompanyAdmin: (state, getters) => {
      const level = getters.userLevel;
      return level === 'ADMIN';
    },
    userCompany: (state, getters) => {
      const user = getters.currentUser;
      const company = user?.bankid;
      if (typeof company === 'string') {
        return company.trim();
      }
      return company || null;
    },
    companyDetails: (state, getters) => {
      const banks = getters.getBanks;
      const user = getters.currentUser;
      const userCompany = user?.bankid;
      const bank = banks.find(bank => bank.id === userCompany);

      if(bank){
        localStorage.setItem('Api-Key', bank.ApiKey);
      }

      return bank ? bank : 'Unknown Bank';
    },
    getKycSignups: (state) => state.kycSignups
  },
  mutations: {
    setLoadingState: (state, payload) => (state.loading = payload),
    setPayments: (state, payments) => (state.payments = payments),
    setDashboardData: (state, payload) => (state.dashboardData = payload),
    setDashboardLoading: (state, payload) => (state.dashboardLoading = payload),
    setDashboardError: (state, payload) => (state.dashboardError = payload),
    setUser: (state, user) => {
      state.user = user;
      state.isAuthenticated = !!user;
    },
    setAllBanks: async (state, payload) => {
      if (payload) await Storage.setBanks(payload)
      else payload = await Storage.getBanks();
      state.banks = payload ? payload : [];
    },
    setAuthStatus: (state, status) => {
      state.isAuthenticated = status;
    },
    setRequestCounts: (state, { agentCount, requestCount }) => {
      state.agentCount = agentCount;
      state.requestCount = requestCount;
    },
    setKycSignups: (state, signups) => (state.kycSignups = signups)
  },
  actions: {
    fetchDashboardData: async ({ commit, getters }) => {
      commit('setDashboardLoading', true);

      try {
        const isSuperAdmin = getters.isSuperAdmin;
        const companyName = getters.userCompany;
        const currentUser = getters.currentUser;

        if (!isSuperAdmin && !companyName) {
          console.warn('[fetchDashboardData] Company name is missing for non-super admin.');
          return null;
        }

        const companyid = currentUser?.bankid;
        if (!isSuperAdmin && !companyid) {
          console.warn('[fetchDashboardData] Bank ID is missing for non-super admin.');
          throw new Error('Bank ID is required for company-level dashboard data.');
        }

        const baseUrl = window?.bankApi;
        if (!baseUrl) {
          console.error('[fetchDashboardData] Missing window.bankApi.');
          return null;
        }

        let summaryRes = null;

        const headers = {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Api-Key': 'f6fef6af-0fca-49fe-bec4-89ddb6d24502'
        };

        if (isSuperAdmin) {
          summaryRes = await axios.get(`${baseUrl}stats/getCount`, { headers });
        } else {
          summaryRes = await axios.get(`${baseUrl}stats/getCount?bankid=${companyid}`, { headers });
        }

        const responseData = summaryRes?.data?.response || {};

        const formattedData = {
          // Super Admin values
          totalApprovedSignups: isSuperAdmin ? responseData.SuccessCount || 0 : 0,
          totalPendingSignups: isSuperAdmin ? responseData.PendingCount || 0 : 0,
          totalAgentEarnings: isSuperAdmin ? responseData.AgentCount || 0 : 0,
          totalWalletBalance: isSuperAdmin ? responseData.totalBalance || 0 : 0,
          totalDeclinedKYCs: isSuperAdmin ? responseData.DeclineCount || 0 : 0,

          // Company Admin values
          companyApprovedSignups: !isSuperAdmin ? responseData.SuccessCount || 0 : 0,
          companyPendingSignups: !isSuperAdmin ? responseData.PendingCount || 0 : 0,
          companyAgentEarnings: !isSuperAdmin ? responseData.AgentCount || 0 : 0,
          companyWalletBalance: !isSuperAdmin ? responseData.balance || 0 : 0,
          companyDeclinedKYCs: !isSuperAdmin ? responseData.DeclineCount || 0 : 0
        };

        commit('setDashboardData', formattedData);
        return formattedData;
      } catch (error) {
        console.error('[fetchDashboardData] Error:', error);
        errorHandler.tomcatError(error);
        return null;
      } finally {
        commit('setDashboardLoading', false);
      }
    },

    pullKycFiles: async ({ getters }, kycId) => {
      try {
        const response = await axios.get(window.bankApi + `kyc/download/${kycId}`);
        return response.data;
      } catch (error) {
        errorHandler.tomcatError(error)
      }
    },
    
    blockPayment: async ({ getters }, paymentId) => {
      try {
        const response = await axios.post(window.bankApi + 'transactions/block', {
          paymentId
        });
        return response.data;
      } catch (error) {
        errorHandler.tomcatError(error)
      }
    },

    getTransactions: async ( context, payload ) => {
      try {
        const response = await axios.get(window.bankApi + `Payment/getPayments`, {
          params: {
            limit: payload.limit ? payload.limit : null,
            blocked: payload.blocked,
            BankId: payload.bankid ? payload.bankid : null,
            from: payload.from,
            to: payload.to
          }
        });

        context.commit('setPayments', response.data.response);
        return response.data.response;
      } catch (error) {
        errorHandler.tomcatError(error)
      }
    },
    
    generateFinancialReport: async ({ getters }, filters) => {
      try {
        const endpoint = getters.userLevel === 'SUPERADMIN' ?
          'reports/generateGlobal' :
          `reports/generateCompany/${getters.currentUser.company.id}`;
        
        const response = await axios.post(window.bankApi + endpoint, filters);
        return response.data;
      } catch (error) {
        errorHandler.tomcatError(error)
      }
    },

    fetchKycSignups: async ({ commit, getters }, payload) => {
      try {
        const isSuperAdmin = getters.isSuperAdmin;

        const currentUser = getters.currentUser;
        let companyid = null;

        if (!isSuperAdmin) {
          if (!currentUser || !currentUser.bankid) {
            console.warn('[fetchKycSignups] Non-super admin user missing or bankid is not set. Aborting request.');
            return;
          }
          companyid = currentUser.bankid;
        }

        const baseUrl = window?.bankApi;
        if (!baseUrl) {
          console.error('[fetchKycSignups] bankApi is undefined on window object.');
          return;
        }

        const endpoint = isSuperAdmin
            ? 'signup/kazangall'
            : `signup/kazangall?bankid=${companyid}`;

        const dateRange = getters.getGlobalDateRange || { from: null, to: null };

        const response = await axios.get(baseUrl + endpoint, {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Api-Key': 'f6fef6af-0fca-49fe-bec4-89ddb6d24502'
          },
          params: {
            limit: payload || null,
            from: payload ? null : dateRange.from,
            to: payload ? null : dateRange.to
          }
        });

        if (!response?.data?.response) {
          console.warn('[fetchKycSignups] No data returned in response.');
          return [];
        }

        commit('setKycSignups', response.data.response);
        return response.data.response;
      } catch (error) {
        console.error('[fetchKycSignups] Error fetching KYC signups:', error);
        errorHandler.tomcatError(error);
        return [];
      }
    },

    getAllBanks: async ( context ) => {
      try {
        const response = await axios.get(window.bankApi + `bank/banks`);
        context.commit('setAllBanks', response.data.response)
      } catch (error) {
        errorHandler.tomcatError(error)
      }
    },

    getSignupAudits: async ( context, payload ) => {
      try {
        const response = await axios.get(bankApi + `audit/signup`, {
          params: {
            id: payload
          }
        });

        return response.data.response
      } catch (error) {
        errorHandler.tomcatError(error)
      }
    },

    refreshState: (context) => {
      return context.dispatch(`syncData`).then(() => {
        return Promise.all([
          context.commit(`setAllBanks`),
        ]);
      });
    },

    syncData: (context) => {
      $utils.showLoading();
      let methods = [
        context.dispatch(`getAllBanks`),
      ];
      return Promise.all(methods).finally(() => $utils.hideLoading());
    },

    getPaymentStats: (context, payload) => {
      try {
        let api = payload.ApiKey;

        if(!api){
          api = 'f6fef6af-0fca-49fe-bec4-89ddb6d24502'
        }

        const res = axios.get(bankApi + `audit/getpaymentstats`, {
          params: {
            bankid: payload.bankid ? payload.bankid : null,
          },
          headers: {
            "Api-Key": api,
          }
        })

        // console.log(res)
        return res;
      } catch (e) {
        errorHandler.tomcatError(e)
      }
    },

    requestPasswordReset: async (context, username) => {
      return axios.post(franchiseTomcatApi + `SetKazangUser/${username}/forgotPasswordRequest`, {
        username: username
      }).catch(err => errorHandler.tomcatError(err))
    },

    resetPassword: async (context, payload) => {
      return axios.post(franchiseTomcatApi + `SetKazangUser/${payload.username}/resetPassword`, payload)
          .then(() => router.push({name: 'login'}))
          .catch(err => errorHandler.tomcatError(err))
    },
  },
  modules: {
    auth: authModule,
    kyc: kycModule,
    payment: paymentModule,
    wallet: walletModule,
    financialReports: financialReportsModule,
    requests,
    agentModule,
    timeModule
  },
});
